import { arrow, computePosition, flip, offset, shift } from '@floating-ui/dom';
import CrossSVG from '@public/icons/cross.svg';
import classNames from 'classnames';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Navigation } from 'swiper';
import type { Swiper as SwiperInstance } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useMediaQueryDeviceStateValue } from '@/shared/atoms/media-query-device';
import Image from '@/shared/components/shared/Image';
import Pagination from '@/shared/components/shared/Pagination';
import Responsive from '@/shared/components/shared/Responsive';
import { useMounted } from '@/shared/hooks/use-mounted';
import { useTypograf } from '@/shared/hooks/use-typograf';
import { ImageShape } from '@/shared/lib/types';

const HALF_SIZE = 5;
const DESKTOP_BP = 1200;
const PREV_BTN = 'speakers-section__prev-btn';
const NEXT_BTN = 'speakers-section__next-btn';

type SpeakersSwiper = SwiperInstance & {
    slidesGrid: number[];
    slidesSizesGrid: number[];
    virtualSize: number;
    size: number;
    destroyed?: boolean;
};

function isSpeakersSwiperAlive(swiper: SwiperInstance | SpeakersSwiper | null | undefined): swiper is SpeakersSwiper {
    return Boolean(swiper && !swiper.destroyed && swiper.params && swiper.el && swiper.slides);
}

function rebuildSpeakersSlidesGrid(swiper: SpeakersSwiper) {
    if (typeof window === 'undefined' || window.innerWidth < DESKTOP_BP || !isSpeakersSwiperAlive(swiper)) {
        return;
    }

    const offsetBefore = typeof swiper.params.slidesOffsetBefore === 'number' ? swiper.params.slidesOffsetBefore : 0;
    const offsetAfter = typeof swiper.params.slidesOffsetAfter === 'number' ? swiper.params.slidesOffsetAfter : 0;

    const slidesGrid: number[] = [];
    const slidesSizesGrid: number[] = [];

    for (let i = 0; i < swiper.slides.length; i += 1) {
        const slide = swiper.slides[i] as HTMLElement;
        slidesGrid.push(slide.offsetLeft - offsetBefore);
    }

    for (let i = 0; i < slidesGrid.length; i += 1) {
        if (i < slidesGrid.length - 1) {
            slidesSizesGrid.push(Math.max(slidesGrid[i + 1] - slidesGrid[i], 0));
        } else {
            const slide = swiper.slides[i] as HTMLElement;
            slidesSizesGrid.push(slide.offsetWidth);
        }
    }

    const snapGrid: number[] = [];
    for (let i = 0; i < slidesGrid.length; i += HALF_SIZE) {
        snapGrid.push(slidesGrid[i]);
    }

    swiper.slidesGrid = slidesGrid;
    swiper.snapGrid = snapGrid;
    swiper.slidesSizesGrid = slidesSizesGrid;

    const lastSlide = swiper.slides[swiper.slides.length - 1] as HTMLElement | undefined;
    if (lastSlide) {
        swiper.virtualSize = lastSlide.offsetLeft + lastSlide.offsetWidth + offsetAfter;
    }

    swiper.navigation?.update?.();
}

function syncSpeakersOverflow(swiper: SwiperInstance, options?: { forceUpdate?: boolean }) {
    if (!isSpeakersSwiperAlive(swiper)) {
        return false;
    }

    const isDesktopViewport = typeof window !== 'undefined' && window.innerWidth >= DESKTOP_BP;

    if (isDesktopViewport) {
        rebuildSpeakersSlidesGrid(swiper);
    } else if (options?.forceUpdate) {
        swiper.update();
    }

    if (!isSpeakersSwiperAlive(swiper)) {
        return false;
    }

    const offsetAfter = typeof swiper.params.slidesOffsetAfter === 'number' ? swiper.params.slidesOffsetAfter : 0;
    const lastSlide = swiper.slides[swiper.slides.length - 1] as HTMLElement | undefined;
    const contentEnd = lastSlide ? lastSlide.offsetLeft + lastSlide.offsetWidth + offsetAfter : 0;
    const overflows = contentEnd > swiper.size + 1;

    swiper.isLocked = !overflows;
    swiper.allowSlideNext = overflows;
    swiper.allowSlidePrev = overflows;
    swiper.allowTouchMove = overflows;
    swiper.el.classList.toggle('swiper-lock', !overflows);

    if (!overflows && swiper.activeIndex !== 0) {
        swiper.slideTo(0, 0);
    }

    swiper.navigation?.update?.();

    return overflows;
}

type SpeakersSectionItem = {
    name?: string | null;
    job?: string | null;
    img?: ImageShape | null;
};

export type SpeakersSectionRawProps = {
    title?: string | null;
    text?: string | null;
    speakers?: SpeakersSectionItem[] | null;
};

type Props = React.HTMLAttributes<HTMLElement> & {
    speakersSectionProps: SpeakersSectionRawProps;
    openOnHover?: boolean;
};

const hasSpeakerContent = (speaker: SpeakersSectionItem) => Boolean(speaker.name || speaker.job);

type SpeakerCardProps = {
    speaker: SpeakersSectionItem;
    index: number;
    isActive: boolean;
    isDesktop: boolean;
    openOnHover: boolean;
    sectionRef: React.RefObject<HTMLElement | null>;
    onActivate: (index: number) => void;
    onDeactivate: () => void;
};

function SpeakerCard({
    speaker,
    index,
    isActive,
    isDesktop,
    openOnHover,
    sectionRef,
    onActivate,
    onDeactivate,
}: SpeakerCardProps) {
    const tpName = useTypograf(speaker.name ?? '');
    const tpJob = useTypograf(speaker.job ?? '');
    const interactive = hasSpeakerContent(speaker);
    const cardRef = useRef<HTMLDivElement | null>(null);
    const infoRef = useRef<HTMLDivElement | null>(null);
    const arrowRef = useRef<HTMLDivElement | null>(null);
    const floatingGenRef = useRef(0);

    const updateFloating = useCallback(async () => {
        const reference = cardRef.current;
        const floating = infoRef.current;
        const arrowEl = arrowRef.current;

        if (!reference || !floating || !isDesktop || !isActive) {
            return;
        }

        const gen = floatingGenRef.current + 1;
        floatingGenRef.current = gen;

        const { x, y, placement, middlewareData } = await computePosition(reference, floating, {
            placement: 'bottom',
            strategy: 'fixed',
            middleware: [
                offset(12),
                flip({
                    padding: { top: 120, bottom: 16, left: 16, right: 16 },
                }),
                shift({ padding: 16 }),
                ...(arrowEl ? [arrow({ element: arrowEl, padding: 8 })] : []),
            ],
        });

        if (floatingGenRef.current !== gen || !infoRef.current) {
            return;
        }

        Object.assign(floating.style, {
            position: 'fixed',
            left: `${x}px`,
            top: `${y}px`,
        });

        if (arrowEl && middlewareData.arrow) {
            const { x: arrowX, y: arrowY } = middlewareData.arrow;
            const side = placement.split('-')[0] as 'top' | 'bottom' | 'left' | 'right';
            const staticSide = {
                top: 'bottom',
                bottom: 'top',
                left: 'right',
                right: 'left',
            }[side];

            Object.assign(arrowEl.style, {
                left: arrowX != null ? `${arrowX}px` : '',
                top: arrowY != null ? `${arrowY}px` : '',
                right: '',
                bottom: '',
                [staticSide]: '-6px',
            });

            arrowEl.dataset.placement = side;
        }
    }, [isActive, isDesktop]);

    useLayoutEffect(() => {
        if (!isActive || !isDesktop) {
            if (infoRef.current) {
                infoRef.current.style.position = '';
                infoRef.current.style.left = '';
                infoRef.current.style.top = '';
            }

            return;
        }

        void updateFloating();
    }, [isActive, isDesktop, updateFloating]);

    useEffect(() => {
        if (!isActive || !isDesktop) {
            return;
        }

        let rafId = 0;
        const onReposition = () => {
            if (rafId) {
                return;
            }

            rafId = requestAnimationFrame(() => {
                rafId = 0;
                void updateFloating();
            });
        };

        const swiperEl = cardRef.current?.closest('.swiper') as (HTMLElement & { swiper?: SwiperInstance }) | null;
        const swiper = swiperEl?.swiper;

        window.addEventListener('resize', onReposition);
        window.addEventListener('scroll', onReposition, true);
        swiper?.on('setTranslate', onReposition);
        swiper?.on('transitionStart', onReposition);
        swiper?.on('transitionEnd', onReposition);

        return () => {
            if (rafId) {
                cancelAnimationFrame(rafId);
            }

            window.removeEventListener('resize', onReposition);
            window.removeEventListener('scroll', onReposition, true);
            swiper?.off('setTranslate', onReposition);
            swiper?.off('transitionStart', onReposition);
            swiper?.off('transitionEnd', onReposition);
        };
    }, [isActive, isDesktop, updateFloating]);

    const handleClick = () => {
        if (!isDesktop || !interactive || openOnHover) {
            return;
        }

        if (isActive) {
            onDeactivate();
        } else {
            onActivate(index);
        }
    };

    const info =
        tpName || tpJob ? (
            <div
                ref={infoRef}
                className={classNames('speakers-section__card-info', {
                    'speakers-section__card-info--open': isDesktop && isActive,
                    'speakers-section__card-info--hover': isDesktop && openOnHover,
                })}
                onClick={openOnHover ? undefined : (event) => event.stopPropagation()}
            >
                <div ref={arrowRef} className="speakers-section__card-arrow" aria-hidden />
                <div className="speakers-section__card-info-inner">
                    <div className="speakers-section__card-info-head">
                        {tpName && <div className="speakers-section__card-name text-l">{tpName}</div>}
                        {interactive && !openOnHover && (
                            <button
                                type="button"
                                className="speakers-section__card-close"
                                aria-label="Закрыть"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onDeactivate();
                                }}
                            >
                                <CrossSVG />
                            </button>
                        )}
                    </div>
                    {tpJob && <div className="speakers-section__card-job text-xs">{tpJob}</div>}
                </div>
            </div>
        ) : null;

    return (
        <div
            ref={cardRef}
            className={classNames('speakers-section__card', {
                'speakers-section__card--interactive': interactive,
                'is-active': isActive,
            })}
            role={interactive && isDesktop && !openOnHover ? 'button' : undefined}
            tabIndex={interactive && isDesktop && !openOnHover ? 0 : undefined}
            onClick={handleClick}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleClick();
                }
            }}
        >
            <div className="speakers-section__card-media">
                {speaker.img?.src ? (
                    <Responsive className="speakers-section__card-img">
                        <Image
                            src={speaker.img.src}
                            alt={speaker.img.alt || tpName || ''}
                            title={speaker.img.title}
                            width={speaker.img.width}
                            height={speaker.img.height}
                            sizes="(max-width: 767px) 54vw, (max-width: 1199px) 34vw, 23vw"
                        />
                    </Responsive>
                ) : (
                    <div className="speakers-section__card-placeholder" />
                )}
            </div>

            {isDesktop
                ? isActive && info && sectionRef.current
                    ? createPortal(info, sectionRef.current)
                    : null
                : info}
        </div>
    );
}

function SpeakersSection({ speakersSectionProps, className, openOnHover = true, ...props }: Props) {
    const { title, text, speakers } = speakersSectionProps;
    const tpTitle = useTypograf(title ?? '');
    const tpText = useTypograf(text ?? '');
    const items = speakers ?? [];
    const device = useMediaQueryDeviceStateValue();
    const isDesktop = device === 'desktop';
    const isMounted = useMounted();

    const sectionRef = useRef<HTMLElement | null>(null);
    const sliderModeRef = useRef<'desktop' | 'touch' | null>(null);
    const lastPointerRef = useRef<{ x: number; y: number } | null>(null);
    const isSliderDraggingRef = useRef(false);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const [paginationIndex, setPaginationIndex] = useState(0);
    const [lastPaginationIndex, setLastPaginationIndex] = useState(0);
    const [isSliderActive, setIsSliderActive] = useState(false);

    const groupSize = isDesktop ? HALF_SIZE : 1;
    const numPages = Math.max(1, Math.ceil(items.length / groupSize));

    const syncHoverFromPoint = useCallback(
        (clientX: number, clientY: number) => {
            if (!openOnHover || !isDesktop) {
                return;
            }

            const section = sectionRef.current;
            if (!section) {
                return;
            }

            const target = document.elementFromPoint(clientX, clientY);
            if (!target || !section.contains(target)) {
                setActiveIndex(null);
                return;
            }

            const card = target.closest('.speakers-section__card--interactive');
            if (!card || !section.contains(card)) {
                setActiveIndex(null);
                return;
            }

            const cards = section.querySelectorAll('.speakers-section__card');
            const nextIndex = Array.from(cards).indexOf(card);
            if (nextIndex < 0) {
                setActiveIndex(null);
                return;
            }

            setActiveIndex((prev) => (prev === nextIndex ? prev : nextIndex));
        },
        [isDesktop, openOnHover],
    );

    const handleSliderSync = (swiper: SwiperInstance) => {
        const mode = typeof window !== 'undefined' && window.innerWidth >= DESKTOP_BP ? 'desktop' : 'touch';
        const modeChanged = sliderModeRef.current !== null && sliderModeRef.current !== mode;
        sliderModeRef.current = mode;
        setIsSliderActive(syncSpeakersOverflow(swiper, { forceUpdate: modeChanged && mode === 'touch' }));
    };

    useEffect(() => {
        if (!openOnHover || !isDesktop) {
            return;
        }

        const onPointerMove = (event: PointerEvent) => {
            lastPointerRef.current = { x: event.clientX, y: event.clientY };

            if (isSliderDraggingRef.current) {
                return;
            }

            const section = sectionRef.current;
            if (!section) {
                return;
            }

            const rect = section.getBoundingClientRect();
            const inside =
                event.clientX >= rect.left &&
                event.clientX <= rect.right &&
                event.clientY >= rect.top &&
                event.clientY <= rect.bottom;

            if (!inside) {
                setActiveIndex((prev) => (prev == null ? prev : null));
                return;
            }

            syncHoverFromPoint(event.clientX, event.clientY);
        };

        document.addEventListener('pointermove', onPointerMove, { passive: true });

        return () => {
            document.removeEventListener('pointermove', onPointerMove);
        };
    }, [isDesktop, openOnHover, syncHoverFromPoint]);

    useEffect(() => {
        if (openOnHover || activeIndex == null || !isDesktop) {
            return;
        }

        const onPointerDown = (event: PointerEvent) => {
            const target = event.target as Node | null;
            const activeCard = sectionRef.current?.querySelector('.speakers-section__card.is-active');
            const activeInfo = document.querySelector('.speakers-section__card-info--open');

            if (activeCard?.contains(target) || activeInfo?.contains(target)) {
                return;
            }

            setActiveIndex(null);
        };

        document.addEventListener('pointerdown', onPointerDown);

        return () => {
            document.removeEventListener('pointerdown', onPointerDown);
        };
    }, [activeIndex, isDesktop, openOnHover]);

    useEffect(() => {
        setActiveIndex(null);
        setPaginationIndex(0);
        setLastPaginationIndex(0);
        setIsSliderActive(false);
    }, [isDesktop]);

    if (items.length === 0 && !tpTitle && !tpText) {
        return null;
    }

    return (
        <section
            {...props}
            ref={sectionRef}
            className={classNames('speakers-section js-header-theme-trigger', className)}
            data-theme="dark"
        >
            <div className="wrapper speakers-section__top">
                {tpTitle && (
                    <div className="speakers-section__title-wrapper">
                        <h2 className="speakers-section__title h3">{tpTitle}</h2>
                    </div>
                )}
                {tpText && <div className="speakers-section__text text-s">{tpText}</div>}
            </div>

            {items.length > 0 && (
                <div className="speakers-section__bottom">
                    {isMounted ? (
                        <Swiper
                            key={isDesktop ? 'desktop' : 'touch'}
                            className="speakers-section__slider"
                            modules={[Navigation]}
                            navigation={{
                                prevEl: `.${PREV_BTN}`,
                                nextEl: `.${NEXT_BTN}`,
                            }}
                            speed={650}
                            slidesPerView="auto"
                            watchOverflow
                            breakpoints={{
                                320: {
                                    slidesPerGroup: 1,
                                    spaceBetween: 8,
                                    slidesOffsetBefore: 16,
                                    slidesOffsetAfter: 16,
                                },
                                768: {
                                    slidesPerGroup: 1,
                                    spaceBetween: 16,
                                    slidesOffsetBefore: 22,
                                    slidesOffsetAfter: 22,
                                },
                                1200: {
                                    slidesPerGroup: HALF_SIZE,
                                    spaceBetween: 0,
                                    slidesOffsetBefore: 70,
                                    slidesOffsetAfter: 70,
                                },
                            }}
                            onAfterInit={(swiper) => {
                                requestAnimationFrame(() => {
                                    if (!isSpeakersSwiperAlive(swiper)) {
                                        return;
                                    }

                                    handleSliderSync(swiper);
                                });
                            }}
                            onResize={(swiper) => {
                                if (!isSpeakersSwiperAlive(swiper)) {
                                    return;
                                }

                                handleSliderSync(swiper);
                            }}
                            onBreakpoint={(swiper) => {
                                requestAnimationFrame(() => {
                                    if (!isSpeakersSwiperAlive(swiper)) {
                                        return;
                                    }

                                    handleSliderSync(swiper);

                                    if (swiper.activeIndex !== 0) {
                                        swiper.slideTo(0, 0);
                                    }
                                });
                            }}
                            onTouchStart={() => {
                                isSliderDraggingRef.current = true;
                            }}
                            onTouchEnd={() => {
                                isSliderDraggingRef.current = false;

                                if (openOnHover && lastPointerRef.current) {
                                    syncHoverFromPoint(lastPointerRef.current.x, lastPointerRef.current.y);
                                }
                            }}
                            onSlideChange={(swiper) => {
                                if (!isSpeakersSwiperAlive(swiper)) {
                                    return;
                                }

                                const perGroup =
                                    typeof swiper.params.slidesPerGroup === 'number' ? swiper.params.slidesPerGroup : 1;
                                const nextPage = Math.floor(swiper.realIndex / perGroup);
                                setLastPaginationIndex(paginationIndex);
                                setPaginationIndex(nextPage);

                                if (openOnHover) {
                                    if (!isSliderDraggingRef.current && lastPointerRef.current) {
                                        syncHoverFromPoint(lastPointerRef.current.x, lastPointerRef.current.y);
                                    }
                                    return;
                                }

                                setActiveIndex(null);
                            }}
                        >
                            {items.map((speaker, index) => (
                                <SwiperSlide className="speakers-section__slide" key={index}>
                                    <SpeakerCard
                                        speaker={speaker}
                                        index={index}
                                        isActive={activeIndex === index}
                                        isDesktop={isDesktop}
                                        openOnHover={openOnHover}
                                        sectionRef={sectionRef}
                                        onActivate={setActiveIndex}
                                        onDeactivate={() => setActiveIndex(null)}
                                    />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    ) : (
                        <div className="wrapper speakers-section__slider-fallback">
                            {items.slice(0, 5).map((speaker, index) => (
                                <div className="speakers-section__slide" key={index}>
                                    <SpeakerCard
                                        speaker={speaker}
                                        index={index}
                                        isActive={false}
                                        isDesktop={false}
                                        openOnHover={openOnHover}
                                        sectionRef={sectionRef}
                                        onActivate={() => undefined}
                                        onDeactivate={() => undefined}
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {items.length > 1 && (
                        <Pagination
                            numSlides={numPages}
                            currentRealIndex={paginationIndex}
                            lastRealIndex={lastPaginationIndex}
                            prevElClass={`${PREV_BTN} slider-prev-btn`}
                            nextElClass={`${NEXT_BTN} slider-next-btn`}
                            wrapperClass={classNames('speakers-section__pagination pagination-wrapper wrapper', {
                                'speakers-section__pagination--inactive': !isSliderActive,
                            })}
                        />
                    )}
                </div>
            )}
        </section>
    );
}

export default SpeakersSection;
